import { useEffect } from 'react';

type ShortcutHandlers = {
  onFocusPrompt: () => void;
  onSend: () => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onToggleFullscreen: () => void;
  onToggleSidebar: () => void;
  onOpenCommandPalette: () => void;
  onOpenShortcutsHelp: () => void;
  onSwitchTab: (tab: string) => void;
  onExport: () => void;
  onToggleProperties: () => void;
  isSending: boolean;
  activeTab: string;
};

export function useKeyboardShortcuts(handlers: ShortcutHandlers) {
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const typing =
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'INPUT' ||
        target.getAttribute('contenteditable') === 'true';
      const meta = event.metaKey || event.ctrlKey;

      // Cmd+K → Command palette (works even when typing)
      if (meta && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        handlers.onOpenCommandPalette();
        return;
      }

      // / → Focus prompt
      if (event.key === '/' && !typing && !meta) {
        event.preventDefault();
        handlers.onFocusPrompt();
        return;
      }

      // ? → Shortcuts help
      if (event.key === '?' && !typing && !meta) {
        event.preventDefault();
        handlers.onOpenShortcutsHelp();
        return;
      }

      // Cmd+Enter → Send
      if (meta && event.key.toLowerCase() === 'enter') {
        if (handlers.activeTab === 'chat' && !handlers.isSending) {
          event.preventDefault();
          handlers.onSend();
        }
        return;
      }

      // Cmd+S → Render/Save
      if (meta && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (handlers.activeTab === 'code') {
          handlers.onSave();
        }
        return;
      }

      // Cmd+B → Toggle sidebar
      if (meta && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        handlers.onToggleSidebar();
        return;
      }

      // Cmd+Z / Cmd+Shift+Z → Undo/Redo
      if (!typing && meta && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          handlers.onRedo();
        } else {
          handlers.onUndo();
        }
        return;
      }

      // f → Toggle fullscreen
      if (!typing && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        handlers.onToggleFullscreen();
        return;
      }

      // 1/2/3 → Switch tabs (Chat/Code/History)
      if (!typing && !meta && (event.key === '1' || event.key === '2' || event.key === '3')) {
        event.preventDefault();
        const tab = event.key === '1' ? 'chat' : event.key === '2' ? 'code' : 'history';
        handlers.onSwitchTab(tab);
        return;
      }

      // e → Toggle export menu
      if (!typing && !meta && event.key.toLowerCase() === 'e') {
        event.preventDefault();
        handlers.onExport();
        return;
      }

      // i → Toggle properties panel
      if (!typing && !meta && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        handlers.onToggleProperties();
      }
    };

    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [handlers]);
}
